import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { showcaseAPI } from '../lib/api';
import { optimizeCloudinaryUrl } from '../lib/cloudinary';

const AUTOPLAY_MS = 4500;
const CAMPAIGN_COLORS = {
  Oferta:   'bg-red-500',
  Novidade: 'bg-emerald-500',
  Destaque: 'bg-amber-500',
  Exclusivo:'bg-violet-600',
};

const formatPrice = (v) =>
  typeof v === 'number' ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v) : null;

/**
 * Vitrine Dinâmica Automática — Etapa 4
 * Carrossel autoplay curado 100% pelo admin. Mostra produtos/lojas alternadamente
 * com imagem OU vídeo curto e navega para /produto/:id ou /loja/:id.
 */
export const DynamicShowcase = () => {
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    let alive = true;
    showcaseAPI
      .getDynamic(30)
      .then(({ data }) => { if (alive) setSlides(data.slides || []); })
      .catch(() => { if (alive) setSlides([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (paused || slides.length < 2) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, slides.length]);

  if (loading || slides.length === 0) return null;

  const go = (dir) => {
    setIndex((i) => (i + dir + slides.length) % slides.length);
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <section
      className="px-4 pt-4"
      data-testid="dynamic-showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-violet-600 text-white shadow-sm">
            <Sparkles className="w-4 h-4" />
          </span>
          Em destaque agora
        </h2>
        <div className="flex gap-1" data-testid="showcase-dots">
          {slides.slice(0, Math.min(slides.length, 8)).map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-6 bg-blue-600' : 'w-1.5 bg-slate-300'
              }`}
              data-testid={`showcase-dot-${i}`}
            />
          ))}
        </div>
      </div>

      <div
        className="relative rounded-2xl overflow-hidden shadow-lg bg-slate-100 aspect-[16/10] md:aspect-[21/9] md:max-h-[420px] max-w-5xl mx-auto"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {slides.map((s, i) => (
          <Link
            key={s.id}
            to={s.link || '#'}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'
            }`}
            data-testid={`showcase-slide-${i}`}
          >
            {s.media_type === 'video' ? (
              <video
                src={s.media_url}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
                data-testid={`showcase-video-${i}`}
              >
                <track kind="captions" />
              </video>
            ) : (
              <img
                src={optimizeCloudinaryUrl(s.media_url, { width: 1200 })}
                alt={s.title || 'Destaque'}
                loading={i === 0 ? 'eager' : 'lazy'}
                className="w-full h-full object-cover"
                data-testid={`showcase-image-${i}`}
              />
            )}

            {/* gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent pointer-events-none" />

            {/* label */}
            {s.campaign_label && (
              <span
                className={`absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest text-white shadow ${
                  CAMPAIGN_COLORS[s.campaign_label] || 'bg-blue-600'
                }`}
                data-testid={`showcase-label-${i}`}
              >
                {s.campaign_label}
              </span>
            )}

            {/* text block */}
            <div className="absolute inset-x-0 bottom-0 p-4 text-white">
              {s.subtitle && (
                <p className="text-[11px] uppercase tracking-widest text-white/80 truncate" data-testid={`showcase-subtitle-${i}`}>
                  {s.subtitle}
                </p>
              )}
              {s.title && (
                <p className="text-lg font-bold leading-tight line-clamp-2" data-testid={`showcase-title-${i}`}>
                  {s.title}
                </p>
              )}
              {(s.promo_price || s.price) && (
                <div className="mt-1 flex items-baseline gap-2" data-testid={`showcase-price-${i}`}>
                  {s.promo_price ? (
                    <>
                      <span className="text-xs text-white/70 line-through">{formatPrice(s.price)}</span>
                      <span className="text-xl font-black text-amber-300">{formatPrice(s.promo_price)}</span>
                    </>
                  ) : (
                    <span className="text-xl font-black">{formatPrice(s.price)}</span>
                  )}
                </div>
              )}
            </div>
          </Link>
        ))}

        {/* nav buttons */}
        {slides.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              aria-label="Slide anterior"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-40 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-slate-800 shadow flex items-center justify-center"
              data-testid="showcase-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => go(1)}
              aria-label="Próximo slide"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-40 w-9 h-9 rounded-full bg-white/85 hover:bg-white text-slate-800 shadow flex items-center justify-center"
              data-testid="showcase-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>
    </section>
  );
};

export default DynamicShowcase;
